import KLineGetDataController from './KLineGetData';
import KLineSetChartController from './KLineSetChart';
import KLineGenerateSeriesController from './KLineGenerateSeries';
import { RealTimeSolutionStatus } from './common/enum';

/**
 * 定义K线类
 */
class KLineController {
    /**
     * K线类构造函数
     * @param {Object} loadChartOption 加载的ECharts选项
     * @param {Object} loadMarkPointOption 加载的MarkPoint选项
     * @param {String} requestModule 请求的服务器端模块（如：kline、future等）
     * @param {Number} timeInterval 请求数据的时间间隔
     */
    constructor({ loadChartOption, loadMarkPointOption, requestModule = '', timeInterval }) {
        this.setChartCtrl = new KLineSetChartController(loadChartOption);
        this.generateSeriesCtrl = new KLineGenerateSeriesController(loadMarkPointOption);
        this.getDataCtrl = new KLineGetDataController(requestModule, timeInterval);

        // 将要显示的series记录下来
        this.showSeries = [];
        // 将要显示的MarkPoint类型记录下来
        this.showMarkPointTypes = [];
        // 将要显示的指标转换成需要请求的数据类型，因为有些指标是通过计算得来的
        this.getDataTypes = [];
    }

    /**
     * 初始化图表
     * @param {HTMLElement} DOM 界面DOM元素
     */
    initChart(DOM) {
        this.setChartCtrl.initECharts(DOM);
    }

    /**
     * 停止定时器
     */
    clearInterval() {
        this.getDataCtrl.clearInterval();
    }

    /**
     * 获取响应数据，以便组件进行监听
     * @returns {Object} 返回数据结果对象
     */
    getResponseData() {
        return this.getDataCtrl.response;
    }

    /**
     * 获取当前Tooltip的数据对象
     * @returns {Array<Object>} 返回series集合数据
     */
    getTooltipDataObj() {
        if (this.setChartCtrl && this.setChartCtrl.chartFormatter) {
            return this.setChartCtrl.chartFormatter.tooltipObj;
        }
        return {};
    }

    /**
     * 显示指标改变事件
     * @param {Array<String>} showIndicators 要显示的指标集合
     * @param {Object} requestParam 请求参数
     */
    showIndicatorsOnChange(showIndicators, requestParam) {
        // 记录旧的需要获取的数据类型，以便与新的比较，来确定是否需要重新获取数据
        let oldGetDataTypes = this.getDataTypes;

        ['showSeries', 'getDataTypes', 'showMarkPointTypes'].forEach(key => {
            this[key] = [];
        });

        // K线必须显示
        showIndicators = ['KLINE'].concat(showIndicators);
        showIndicators.forEach(indicator => {
            switch (indicator) {
                case 'KLINE':
                case 'PRESSURELINE':
                case 'NETFLOW':
                    this.getDataTypes.push(indicator);
                    this.showSeries.push(indicator);
                    break;
                case 'REALTIMESOLUTION':
                case 'ABNORMALTRANSACTION':
                    this.getDataTypes.push(indicator);
                    this.showMarkPointTypes.push(indicator.toLowerCase());
                    break;
                default:
                    this.showSeries.push(indicator);
                    break;
            }
        });

        let isNeedRefreshData = this.getDataTypes.some(type => {
            return oldGetDataTypes.indexOf(type) === -1;
        });
        let oldCondition = requestParam;
        if (isNeedRefreshData) {
            oldCondition = {};
        }
        this.requestParamOnChange(requestParam, oldCondition);
    }

    /**
     * 请求参数发生改变事件
     * @param {Object} newCondition 新的选中条件
     * @param {Object} oldCondition 旧的选中条件
     */
    requestParamOnChange(newCondition, oldCondition) {
        let isNeedRefreshData = ['base', 'quote', 'exchange', 'type'].some(key => {
            return newCondition[key] !== oldCondition[key];
        });

        // 是否需要刷新数据
        if (isNeedRefreshData || this.generateSeriesCtrl.times.length === 0) {
            // 启动定时器，组件通过getResponseData监听返回的数据对象，来调用getDataSuccess方法
            this.getDataCtrl.startInterval(newCondition, this.getDataTypes);
        } else {
            this.bindDataToSeriesOption(newCondition);
        }
    }

    /**
     * 获取K线数据成功后的处理逻辑
     * @param {Object} requestParam 选择的条件
     * @param {Object} dataObj 返回的数据对象({kline, price, markpoint})
     * @param {Boolean} isChangeZoom 是否改变数据缩放滑块(默认：true)
     */
    getDataSuccess(requestParam, dataObj, isChangeZoom = true) {
        if (!dataObj || Object.keys(dataObj).length === 0) {
            return;
        }

        // 重置所有属性（包含指标），当数据与选项绑定时，根据选项决定哪些指标需要计算
        this.generateSeriesCtrl.initDataProperty(dataObj);

        this.bindDataToSeriesOption(requestParam);

        if (isChangeZoom) {
            this.setChartCtrl.resetDataZoom(this.generateSeriesCtrl.times);
        }
    }

    /**
     * 设置图表数据
     * @param {Object} requestParam 选择的条件
     */
    bindDataToSeriesOption(requestParam = {}) {
        // // 处理实际要显示哪些MarkPoint
        // let realShowMarkPointTypes = [];
        // this.showMarkPointTypes.forEach(type => {
        //     switch (type) {
        //         case 'REALTIMESOLUTION':
        //             // 是否显示实时解盘
        //             if (selectedCondition.realTimeSolutionStatus === RealTimeSolutionStatus.checked) {
        //                 realShowMarkPointTypes.push(type);
        //             }
        //             break;
        //         default:
        //             realShowMarkPointTypes.push(type);
        //             break;
        //     }
        // });

        let option = this.setChartCtrl.getInitOption(this.generateSeriesCtrl.times, requestParam.type);

        this.showSeries.forEach(seriesName => {
            // // 如果有动态seriesName，则处理，否则使用实际seriesName
            // let realSeriesName = seriesName;
            // switch (seriesName) {
            //     case 'indicator1':
            //     case 'indicator2':
            //         realSeriesName = selectedCondition[seriesName];
            //         break;
            // }

            // 生成series选项
            let seriesOptions = this.generateSeriesCtrl.generateSeriesOption(seriesName, this.showMarkPointTypes);

            // 有的指标会生成多个series，有的只有一个
            if (!(seriesOptions instanceof Array)) {
                seriesOptions = [seriesOptions];
            }
            // 将series选项放到ECharts的series中
            seriesOptions.forEach(seriesOption => {
                if (Object.keys(seriesOption).length === 0) {
                    return;
                }
                option.series.push(seriesOption);
            });
        });

        this.setChartCtrl.applyOptionToChart(
            option,
            this.generateSeriesCtrl.closes,
            option.series.length > 5 ? option.series[5].data : []
        );
    }
}

export default KLineController;



// WEBPACK FOOTER //
// ./src/controller/KLine.js